/**
 * Client-side search over the site's whole corpus (P-SEARCH).
 *
 * The Greek pipeline — normalize, phonetic folding, Greeklish transliteration,
 * tiered scoring — is copied from av-sweetandbitter-web/lib/frontend/search.ts,
 * where it is proven in production against the same language problem.
 *
 * What deliberately differs from sweetandbitter: candidate selection. That
 * site searches hundreds of recipes and needs Strapi (a lifecycle-maintained
 * `searchText` haystack) to pre-filter. This site's corpus is ~50 documents
 * per locale across six types, so the ENTIRE index ships to the client as one
 * small JSON (/api/search-index) and every keystroke matches locally —
 * zero-latency suggestions, no CMS round-trip. If bodies ever need indexing,
 * the sweetandbitter lifecycle approach is the documented fallback.
 *
 * This module is client-safe: no env vars, no tokens, no server imports.
 */

export type SearchDocType =
  | "treatment"
  | "condition"
  | "question"
  | "post"
  | "team"
  | "page";

export interface SearchDoc {
  type: SearchDocType;
  /** absolute path, locale-relative (Next's router adds the locale prefix) */
  url: string;
  title: string;
  /** one-line context shown under the title (teaser / symptoms / category) */
  context?: string;
  /** extra matchable text that must not be displayed (aliases, symptoms…) */
  aliases?: string;
  /** taxonomy names (post categories, question category) */
  taxonomy?: string;
}

export const normalizeSearchText = (s: string): string =>
  foldGreekPhonetic(
    (s ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      // apostrophe variants collapse: the «Κατ' οίκον» chip (ASCII ') was
      // failing against «κατ’ Οίκον» (typographic ’) — exact-substring
      // matching means the two must normalize identically
      .replace(/['\u2019\u02BC]/g, "")
      .replace(/ς/g, "σ")
  );

/**
 * Phonetic folding: identical-sounding Greek spellings collapse to one form
 * (ω→ο, η/υ/ει/οι→ι, αι→ε; ου is protected). This is what lets Greeklish
 * queries match after transliteration — "osfyalgia" must find «οσφυαλγία».
 */
export const foldGreekPhonetic = (s: string): string =>
  s
    .replace(/ει/g, "ι")
    .replace(/οι/g, "ι")
    .replace(/αι/g, "ε")
    .replace(/ου/g, "\u0000")
    .replace(/[ηυ]/g, "ι")
    .replace(/ω/g, "ο")
    .replace(/\u0000/g, "ου");

// Greeklish → Greek (query side only). Digraphs first; the phonetic fold
// afterwards absorbs most transliteration ambiguity (h/η, w/ω, y/υ…).
const GREEKLISH_DIGRAPHS: [RegExp, string][] = [
  [/th/g, "θ"], [/ch/g, "χ"], [/ph/g, "φ"], [/ps/g, "ψ"], [/ks/g, "ξ"],
  [/ou/g, "ου"], [/mp/g, "μπ"], [/nt/g, "ντ"], [/gk/g, "γκ"], [/tz/g, "τζ"],
  [/ts/g, "τσ"], [/ei/g, "ει"], [/oi/g, "οι"], [/ai/g, "αι"],
];
const GREEKLISH_SINGLES: Record<string, string> = {
  a: "α", b: "μπ", c: "κ", d: "δ", e: "ε", f: "φ", g: "γ", h: "η", i: "ι",
  j: "τζ", k: "κ", l: "λ", m: "μ", n: "ν", o: "ο", p: "π", q: "κ", r: "ρ",
  s: "σ", t: "τ", u: "υ", v: "β", w: "ω", x: "χ", y: "υ", z: "ζ",
};

export const greeklishToGreek = (term: string): string => {
  let out = term;
  for (const [re, gr] of GREEKLISH_DIGRAPHS) {
    out = out.replace(re, gr);
  }
  out = out.replace(/[a-z]/g, (c) => GREEKLISH_SINGLES[c] ?? c);
  return foldGreekPhonetic(out);
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const startsAWord = (haystack: string, term: string): boolean =>
  new RegExp(`(^|[^a-z0-9α-ω])${escapeRegExp(term)}`).test(haystack);

/**
 * Each raw term becomes match variants: the normalized term itself (Greek
 * queries; also matches Latin words like "tecar"), plus — when the term is
 * Latin — its Greeklish transliteration («mesi» → «μεσι» → «μέση»).
 */
export const parseSearchTerms = (query: string): string[][] =>
  normalizeSearchText(query)
    .split(/\s+/)
    .filter((t) => t.length >= 2)
    .slice(0, 6)
    .map((t) => {
      const variants = [t];
      if (/[a-z]/.test(t)) {
        const gr = greeklishToGreek(t);
        if (gr !== t) {
          variants.push(gr);
        }
      }
      return variants;
    });

// weight by match location; a word-boundary hit gets a 1.5× bonus.
// Aliases sit ABOVE taxonomy: they exist precisely to catch the colloquial
// symptom words («μέση», «σβέρκος») that titles never contain.
const TIERS: { weight: number; text: (d: SearchDoc) => string }[] = [
  { weight: 100, text: (d) => d.title },
  { weight: 60, text: (d) => d.aliases ?? "" },
  { weight: 50, text: (d) => d.taxonomy ?? "" },
  { weight: 25, text: (d) => d.context ?? "" },
];

/** normalized haystacks, precomputed once per index load */
export interface IndexedDoc extends SearchDoc {
  _h: string[];
}

export const prepareIndex = (docs: SearchDoc[]): IndexedDoc[] =>
  docs.map((d) => ({
    ...d,
    _h: TIERS.map((t) => normalizeSearchText(t.text(d))),
  }));

const scoreDoc = (doc: IndexedDoc, terms: string[][]): number => {
  let total = 0;
  for (const variants of terms) {
    let best = 0;
    for (let i = 0; i < TIERS.length; i++) {
      for (const v of variants) {
        if (!doc._h[i].includes(v)) {
          continue;
        }
        const bonus = startsAWord(doc._h[i], v) ? 1.5 : 1;
        best = Math.max(best, TIERS[i].weight * bonus);
      }
    }
    if (best === 0) {
      return 0; // every term must land somewhere
    }
    total += best;
  }
  return total;
};

export interface SearchHit extends SearchDoc {
  score: number;
}

/** the display order of groups — clinical content first, then support */
export const GROUP_ORDER: SearchDocType[] = [
  "treatment", "condition", "question", "post", "team", "page",
];

export const searchIndex = (
  index: IndexedDoc[],
  query: string,
  limit = 30
): SearchHit[] => {
  const terms = parseSearchTerms(query);
  if (terms.length === 0) {
    return [];
  }
  return index
    .map((d) => ({ ...d, score: scoreDoc(d, terms) }))
    .filter((h) => h.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        GROUP_ORDER.indexOf(a.type) - GROUP_ORDER.indexOf(b.type)
    )
    .slice(0, limit);
};

// Greek → Latin for URL fragments (ELOT-ish, lossy on purpose)
const GREEK_TO_LATIN: Record<string, string> = {
  α: "a", β: "v", γ: "g", δ: "d", ε: "e", ζ: "z", η: "i", θ: "th", ι: "i",
  κ: "k", λ: "l", μ: "m", ν: "n", ξ: "x", ο: "o", π: "p", ρ: "r", σ: "s",
  τ: "t", υ: "y", φ: "f", χ: "ch", ψ: "ps", ω: "o",
};

/**
 * Stable-enough anchor id for an FAQ entry, derived from its question text.
 * Used by BOTH the FAQ page (assigns ids) and the search index (deep-links) —
 * one function so they can never disagree. Rewording a question changes its
 * anchor; the link then lands on the page top, which is acceptable.
 */
export const faqAnchor = (question: string): string =>
  "faq-" +
  (question ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ς/g, "σ")
    .replace(/[α-ω]/g, (c) => GREEK_TO_LATIN[c] ?? c)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);

/** hits regrouped for display, groups in GROUP_ORDER, capped per group */
export const groupHits = (
  hits: SearchHit[],
  perGroup = Infinity
): { type: SearchDocType; hits: SearchHit[] }[] =>
  GROUP_ORDER.map((type) => ({
    type,
    hits: hits.filter((h) => h.type === type).slice(0, perGroup),
  })).filter((g) => g.hits.length > 0);
